import { mdiAccount } from '@mdi/js';
import { Tooltip } from '@mollybet/ui';
import { BaseTable, ColumnProp } from 'common/components/table/base';
import { TableTitle } from 'common/components/tables';
import { CurrencyCodes } from 'common/configs/currency';
import { Customer } from 'common/state/customers/actions';
import { ProviderSummary } from 'common/state/provider/actions';
import React, { useMemo } from 'react';
import styled from 'styled-components';

export interface CustomerInfoTableProps {
  customer?: Customer;
  summaries?: ProviderSummary[];
  isLoading?: boolean;
  error?: React.ReactNode;
}

export interface InfoTableRow extends Customer {
  currencies: CurrencyCodes[];
}

export const CurrencyElement = styled.span<{ isDefault?: boolean }>`
  margin-right: 0.25rem;
  font-weight: ${({ isDefault }) => (isDefault ? 'bold' : undefined)};
`;

export const CustomerInfoTable = ({
  customer,
  summaries,
  isLoading,
  error,
}: CustomerInfoTableProps) => {
  const columns = useMemo(
    (): ColumnProp<InfoTableRow>[] => [
      {
        key: 'currencies',
        title: 'Currencies',
        valueFormatter: (row) =>
          row.currencies.map((currency) =>
            currency === customer?.default_currency ? (
              <Tooltip key={currency} content="Default Currency">
                <CurrencyElement isDefault>{currency}</CurrencyElement>
              </Tooltip>
            ) : (
              <CurrencyElement key={currency}>{currency}</CurrencyElement>
            )
          ),
      },
      { key: 'username', title: 'Username' },
      { key: 'first_name', title: 'First Name' },
      { key: 'last_name', title: 'Last Name' },
      { key: 'email', title: 'Email' },
    ],
    [customer]
  );

  const rows = useMemo(() => {
    if (!summaries || !customer) return null;

    const currencies: Set<CurrencyCodes> = customer?.default_currency
      ? new Set([customer.default_currency])
      : new Set();

    summaries?.forEach((summary) => currencies.add(summary.ccy_code));

    return [{ ...customer, currencies: Array.from(currencies).sort() }];
  }, [customer, summaries]);

  return (
    <>
      <TableTitle
        icon={mdiAccount}
        title={`Provider: ${customer?.username || 'Loading...'}`}
      />
      <BaseTable
        columns={columns}
        rows={rows || []}
        csvName={customer?.username}
        isLoading={isLoading}
        error={error}
        isSortable={false}
        paginate={false}
      />
    </>
  );
};
